import { useMemo, useState } from "react";
import { FaPlus } from "react-icons/fa";
import { FiLayers } from "react-icons/fi";

import { SectionsModels } from "../../../models/SectionsModels";
import { useAdminApi } from "../Admin/hooks/useAdminApi";
import { useAdminDelete } from "../Admin/hooks/useAdminDelete";

import SectionsTable from "./components/SectionsTable";
import Button from "../../../components/Atoms/Button";
import { Modal } from "../../../components/Atoms/Modal";
import {
  PageHeader,
  EmptyState,
  ErrorState,
  SkeletonTable,
  Confirm,
  useAdminResource,
} from "../../../components/Admin";
import AddSection from "./components/AddSection";

import { ListContainer } from "../../_layout/AdminLayout.styles";

/** Ordena pela posição na home; sem posição vai para o fim. */
const byPosition = (a: SectionsModels, b: SectionsModels) =>
  (Number(a.position) || Infinity) - (Number(b.position) || Infinity);

const AdminSections = () => {
  const { fetchSections } = useAdminApi();
  const { deleteItem, deletingId } = useAdminDelete();

  const { data, setData, isLoading, loadError, reload } = useAdminResource<
    SectionsModels,
    unknown
  >({
    fetchPage: () => fetchSections(),
    select: (response) => (Array.isArray(response) ? response : []),
    errorMessage: "Não foi possível buscar a lista de seções.",
  });

  const [openModal, setOpenModal] = useState<boolean>(false);
  const [pendingSection, setPendingSection] = useState<SectionsModels | null>(
    null
  );

  const sections = useMemo(() => [...data].sort(byPosition), [data]);

  const handleModalState = () => setOpenModal((prevState) => !prevState);

  const confirmDelete = async () => {
    const section = pendingSection;
    setPendingSection(null);
    if (!section?.schoolid_sectionid) return;

    const done = await deleteItem({
      entity: "section",
      id: section.schoolid_sectionid,
      successTitle: "Seção excluída",
    });

    if (done) {
      setData((items) =>
        items.filter(
          (item) => item.schoolid_sectionid !== section.schoolid_sectionid
        )
      );
    }
  };

  const showInitialLoading = isLoading && data.length === 0 && !loadError;

  return (
    <>
      <PageHeader
        title="Seções"
        subtitle="Blocos que organizam os cursos na home dos alunos."
        actions={
          <Button onClick={handleModalState}>
            <FaPlus />
            Nova Seção
          </Button>
        }
      />

      <ListContainer>
        {showInitialLoading ? (
          <SkeletonTable rows={4} columns={3} />
        ) : loadError ? (
          <ErrorState message={loadError} onRetry={reload} />
        ) : sections.length === 0 ? (
          <EmptyState
            icon={<FiLayers size={40} />}
            title="Nenhuma seção cadastrada"
            description="Crie uma seção para agrupar cursos na home."
            action={
              <Button onClick={handleModalState}>
                <FaPlus />
                Criar seção
              </Button>
            }
          />
        ) : (
          <SectionsTable
            data={sections}
            onDelete={setPendingSection}
            deletingId={deletingId}
          />
        )}
      </ListContainer>

      <Modal isOpen={openModal}>
        <AddSection onClose={handleModalState} refreshFeth={reload} />
      </Modal>

      <Confirm
        open={pendingSection !== null}
        danger
        title="Excluir seção"
        description={`A seção "${pendingSection?.title}" sai da home. Só é possível se nenhum curso ativo estiver nela. Não dá para desfazer.`}
        confirmLabel="Excluir"
        loading={!!deletingId}
        onConfirm={confirmDelete}
        onCancel={() => setPendingSection(null)}
      />
    </>
  );
};

export default AdminSections;
